import { useId, type KeyboardEvent, type ReactNode } from 'react';

import { cn } from '@shared/lib/cn';

export interface TabItem<T extends string> {
  value: T;
  label: string;
  content: ReactNode;
}

export interface BaseTabsProps<T extends string> {
  tabs: readonly TabItem<T>[];
  value: T;
  onChange: (value: T) => void;
  'aria-label': string;
}

/** Accessible tabs (arrow keys move between tabs). Only the active panel is rendered. */
export function BaseTabs<T extends string>({
  tabs,
  value,
  onChange,
  'aria-label': ariaLabel,
}: BaseTabsProps<T>) {
  const base = useId();
  const active = tabs.find((t) => t.value === value) ?? tabs[0];
  const move = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    const delta = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (!delta) return;
    e.preventDefault();
    const next = tabs[(i + delta + tabs.length) % tabs.length];
    if (next) {
      onChange(next.value);
      document.getElementById(`${base}-tab-${next.value}`)?.focus();
    }
  };
  return (
    <div className="flex flex-col gap-3">
      <div role="tablist" aria-label={ariaLabel} className="flex gap-1 border-b border-line">
        {tabs.map((t, i) => {
          const selected = t.value === active?.value;
          return (
            <button
              key={t.value}
              id={`${base}-tab-${t.value}`}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={`${base}-panel`}
              tabIndex={selected ? 0 : -1}
              onClick={() => {
                onChange(t.value);
              }}
              onKeyDown={(e) => {
                move(e, i);
              }}
              className={cn(
                '-mb-px cursor-pointer border-b-2 px-3 py-2 text-[12px] font-semibold tracking-[0.04em]',
                selected ? 'border-accent text-accent' : 'border-transparent text-text-3 hover:text-text',
              )}
            >
              {t.label}
            </button>
          );
        })}
      </div>
      {active && (
        <div id={`${base}-panel`} role="tabpanel" aria-labelledby={`${base}-tab-${active.value}`}>
          {active.content}
        </div>
      )}
    </div>
  );
}
